import { memo, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { handleDomBubbleEvent } from '@/utils/dom';
import { useUserModalRootActionsStore } from '@/features/users/_shared/components/user-modal-root/user-modal-root.store';
import { DeleteTeamValidationSchema, TDeleteTeamValidation, useDeleteTeam } from '../../../api/delete-team.api';
import { GET_LIST_TEAM_QUERY_KEY } from '../../../api/get-list-team-api';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
  ModalCloseButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';
import type { AxiosErrorCustom } from '@/lib/axios';

export const TOAST_MESSAGES_DELETE_TEAM = {
  DELETE_TEAM_ERROR: 'Delete team failed!',
  DELETE_TEAM_SUCCESS: 'Team deleted successfully!',
};

export type TDeleteTeamModalType = 'DeleteTeamModal';
export type IDeleteTeamModalProps = {
  teamId: string;
};

const DeleteTeamModal = memo(({ teamId }: IDeleteTeamModalProps) => {
  const userModalRootActions = useUserModalRootActionsStore();
  const showToast = useCustomToast();

  const isOpenDeleteTeamModal = useMemo(() => {
    return userModalRootActions.checkIsOpenModal('DeleteTeamModal');
  }, [userModalRootActions]);

  const form = useForm<TDeleteTeamValidation>({
    mode: 'onChange',
    resolver: zodResolver(DeleteTeamValidationSchema),
  });

  useEffect(() => {
    if (teamId) {
      form.reset({
        teamId,
      });
    }
  }, [teamId, form]);

  const mutationDeleteTeam = useDeleteTeam({
    mutationConfig: {
      onError: (error: AxiosErrorCustom) => {
        const errorMessage = axiosExtractErrorMessage(error) || TOAST_MESSAGES_DELETE_TEAM.DELETE_TEAM_ERROR;
        showToast({ type: 'error', message: errorMessage });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [GET_LIST_TEAM_QUERY_KEY] });
        form.reset();
        userModalRootActions.closeModal('DeleteTeamModal');
        showToast({ type: 'success', message: TOAST_MESSAGES_DELETE_TEAM.DELETE_TEAM_SUCCESS });
      },
    },
  });

  const onSubmit = useCallback(
    (data: TDeleteTeamValidation) => {
      if (data.teamId) {
        mutationDeleteTeam.mutate({
          teamId: data.teamId,
        });
      }
    },
    [mutationDeleteTeam]
  );

  const handleClose = useCallback(() => {
    form.reset();
    userModalRootActions.closeModal('DeleteTeamModal');
  }, [form, userModalRootActions]);
  return (
    <Modal isOpen={isOpenDeleteTeamModal} lockBodyScroll onClose={handleClose} className="p-[32px] w-[500px]">
      <ModalHeader>
        <ModalTitle>Delete Team</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(onSubmit)(e);
          }}
          id="delete-team-form"
          className="grid gap-[16px]"
        >
          <span className="text-[14px] font-normal text-[#9D9D9C]">Do you want to delete this team?</span>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="delete-team-form" type="submit" disabled={mutationDeleteTeam.isPending}>
          {mutationDeleteTeam.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null} Delete
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});

DeleteTeamModal.displayName = 'DeleteTeamModal';
export default DeleteTeamModal;
